/**
 * `IWorkflowRunModel.ts` — web port of Unity's `Models/WorkflowModel/IWorkflowRunModel.cs`.
 *
 * Thin run sequencer for the guided-training workflow: starts/stops with play mode, tracks the
 * current/completed steps, auto-plays a step's on-start script when it becomes current, and
 * writes per-user progress to DynamoDB through `IProjectWorkflowModel`.
 */

import type { Emitter } from "@core/events/emitter";
import type { WorkflowRunState, WorkflowUserInputKind } from "./WorkflowDocument";

/** Where the current step points in the scene (the 3D connector's target). */
export interface WorkflowPlaybackAttachment {
  /** Slash-joined display-name path of the part, "" when only a point is set. */
  readonly part: string;
  /** Exact world point on that part, or null to use the part's centre. */
  readonly point: { readonly x: number; readonly y: number; readonly z: number } | null;
}

/**
 * The image or video the current step shows beside the play panel. Resolved
 * from the step's `media` block, with the KIND already decided (explicit, else
 * inferred from the url) and the timecodes already parsed to seconds, so the
 * panel renders it without re-deriving either.
 */
export interface WorkflowPlaybackMedia {
  readonly kind: "image" | "video";
  /**
   * Whether the surface may CROP this media to fill its stage. `"fill"` (the
   * authored default) leaves the framing to the surface; `"contain"` is a
   * contract that it is never cropped anywhere — see `MEDIA_FITS`.
   */
  readonly fit: "contain" | "fill";
  /** The authored url, unresolved — the panel resolves `campfire://` itself. */
  readonly url: string;
  /** Display label; empty when the author gave none (the panel falls back to the step name). */
  readonly name: string;
  readonly description: string;
  /** Video only; empty otherwise. */
  readonly transcript: string;
  /** Video only: seconds, or null when the author set no trim. */
  readonly startSeconds: number | null;
  readonly endSeconds: number | null;
  /**
   * Video only (false otherwise): the clip restarts from its start each time it
   * reaches its end, instead of stopping there.
   *
   * ⚠ The panel must NOT implement this with the `<video loop>` attribute. A
   * looping element fires no `ended`, and that event is the only reliable
   * signal that one full playback has happened — which is what a
   * `video_finished` completion condition opens on. See `useWorkflowMedia`.
   */
  readonly loop: boolean;
  /** Video only (false otherwise): start when the step arrives. */
  readonly autoplay: boolean;
}

/**
 * A command for the video the CURRENT step is showing — the Guide's half of the
 * media panel's own playback cluster, issued through `control_media`.
 *
 * `seek` is RELATIVE and signed, because that is the form the learner asks for
 * out loud most of the time ("go back a bit", "skip ahead").
 *
 * ⚠ `seek_to` is ABSOLUTE, and it REVERSES this type's original note that an
 * absolute position was "deliberately absent: the 2D panels print no timecode,
 * so there is no number the learner could name". That reasoning was about what
 * the learner can READ; it says nothing about what they can ASK ("skip to the
 * middle", "start it at a minute in"), and the agent has the numbers either
 * way — every reply carries the position and the clip's length, so it can turn
 * a described point into one. Both are clamped into the authored clip, so
 * neither can leave it.
 *
 * `expand` / `collapse` are the PRESENTATION rather than the playback: on a
 * step that names a scene the video sits in a small card that does not play on
 * its own, and "play the video" means show it full size and start it. They are
 * refused on a surface that has no such distinction (an XR slate) and on a
 * media-only step, whose panel IS the step.
 *
 * `play` at (or past) the clip's end means "again", exactly as the panel's own
 * play button does — otherwise the trim guard pauses it back within a frame and
 * the command looks like it did nothing.
 */
export type WorkflowMediaCommand =
  | { readonly kind: "replay" }
  | { readonly kind: "play" }
  /** Authored `play_video`: expand a scene-step video and collapse it at its end. */
  | { readonly kind: "play_from_script" }
  | { readonly kind: "pause" }
  /** Pause AND rewind to the clip's start — what "stop" means in any player. */
  | { readonly kind: "stop" }
  | { readonly kind: "seek"; readonly deltaSeconds: number }
  /** Seconds from the AUTHORED clip's start, the frame every reply quotes. */
  | { readonly kind: "seek_to"; readonly positionSeconds: number }
  | { readonly kind: "expand" }
  | { readonly kind: "collapse" }
  /**
   * Internal, never reachable by the agent: put the video back the way a fresh
   * ARRIVAL at the step shows it — paused on the clip's start, docked, unplayed
   * — and start it again if the step autoplays. Issued by
   * {@link IWorkflowRunModel.replayCurrentStep} before it re-arrives, because a
   * same-step arrival changes nothing the panel's media watchers key on, so
   * without it the clip would resume mid-way rather than play again.
   */
  | { readonly kind: "reset_to_arrival" };

/**
 * Where the step's video is after a {@link WorkflowMediaCommand}, measured in
 * the AUTHORED clip's own frame — seconds from `media.start`, not from the
 * file's beginning — so a trimmed clip reads the same here as it does on the
 * panel's readout.
 */
export interface WorkflowMediaPlaybackState {
  readonly positionSeconds: number;
  /**
   * The authored clip's length, or null while it is not known: an untrimmed
   * clip whose metadata has not landed yet has no honest answer, and inventing
   * one would be a duration the Guide could quote.
   */
  readonly durationSeconds: number | null;
  readonly isPlaying: boolean;
  /**
   * Whether the panel is showing the media FULL SIZE rather than as the small
   * card beside the step — or null on a surface with no such distinction (an XR
   * slate, which places its panel in metres and has no expanded state).
   *
   * Null rather than false for the same reason {@link durationSeconds} is null
   * before metadata: "this surface has no answer" is a different fact from "it
   * is collapsed", and reporting the second would let the agent offer to expand
   * something that cannot be.
   */
  readonly isExpanded: boolean | null;
}

/**
 * The outcome of {@link IWorkflowRunModel.controlStepMedia}. A discriminated
 * refusal rather than a bare false, because the four ways it can fail need
 * completely different things said to the learner — and the wording belongs to
 * the executor, not here.
 */
export type WorkflowMediaControlResult =
  | { readonly outcome: "ok"; readonly state: WorkflowMediaPlaybackState }
  /** No guided run is playing, so there is no step to have media. */
  | { readonly outcome: "not_running" }
  /** The current step shows nothing. */
  | { readonly outcome: "no_media" }
  /** The current step shows an IMAGE, which has no playback to control. */
  | { readonly outcome: "not_video" }
  /**
   * There is a video on this step and it cannot be commanded right now: no
   * media surface is mounted, or its element has not loaded the file (still
   * fetching, or it failed). Transient, unlike the three above.
   */
  | { readonly outcome: "unavailable" };

/**
 * The media panel's playback controls, handed to the run model so the agent can
 * press them.
 *
 * The registered-host pattern, in the direction `IPreviewCaptureHost` already
 * establishes — the model owns WHETHER a command is legal (is a run playing,
 * does this step show a video) and the panel owns the `<video>` element, which
 * a headless model must never touch. The inverse direction on the same subject
 * is {@link IWorkflowRunModel.reportStepMediaFinished}.
 */
export interface IWorkflowMediaPlaybackHost {
  /**
   * Runs the command and answers where the video ended up. Null when there is
   * no loaded video element to command — the panel is showing a spinner, an
   * error, or an image — which the model reports as `unavailable`.
   */
  controlMedia(command: WorkflowMediaCommand): WorkflowMediaPlaybackState | null;
}

/** What the workflow OUTLINE — the Groups/Steps map — is doing right now. */
export interface WorkflowOutlineState {
  readonly isVisible: boolean;
  /**
   * How many rows it would show (group headings plus their steps). Zero means
   * there is nothing to show — no workflow, or one whose every step is parked —
   * and showing it would open an empty panel.
   */
  readonly entryCount: number;
}

/**
 * The workflow OUTLINE panel's own toggle, handed to the run model so the agent
 * can press it — `Toggle_Workflow_Outline`'s whole implementation.
 *
 * ⚠ The outline is NOT run-only, and this deliberately does not gate on
 * {@link IWorkflowRunModel.isRunning}: the same panel is a popover from the
 * workflow EDITOR when nothing is playing, and one flag
 * (`useWorkflow.isOutlineOpen`) drives both. It lands on the run model because
 * that is the workflow subsystem's agent-facing model — the one every workflow
 * tool already goes through, and where {@link IWorkflowMediaPlaybackHost}
 * already established a panel control — not because the outline belongs to a
 * run. The host is `useWorkflow`, which owns the flag in both modes.
 *
 * Same disposer contract as the media host, and for the same reason: nothing
 * orders an outgoing panel's teardown after its replacement's setup.
 */
export interface IWorkflowOutlineHost {
  getOutlineState(): WorkflowOutlineState;
  setOutlineVisible(visible: boolean): void;
}

/** One trigger button the Workflow panel renders for the current step. */
export interface WorkflowPlaybackTriggerView {
  /**
   * Index into the step's SOURCE `triggers` list — the activation key.
   * Buttons must not dispatch by label: duplicate labels are only a warning,
   * and a label lookup would send the second button to the first one's target.
   */
  readonly index: number;
  /** Button label the learner sees. */
  readonly label: string;
  /** The authored `go_to` target (a step or group name), for tooltips/telemetry. */
  readonly target: string;
}

/**
 * Everything the Workflow panel needs to render the current playback moment.
 * Derived fresh from the loaded document + run state on every read, so a
 * mid-run builder edit is reflected immediately.
 */
export interface WorkflowPlaybackView {
  readonly workflowName: string;
  readonly stepName: string;
  /** Containing group name, or null for an ungrouped step. */
  readonly groupName: string | null;
  /** 1-based position within the position scope: the step's group when it has one, else all steps. */
  readonly positionIndex: number;
  readonly positionCount: number;
  /** 1-based position across ALL steps in document order. */
  readonly overallIndex: number;
  readonly overallCount: number;
  /**
   * The step's `step_instructions` copy — what the learner is asked to do here
   * (may be empty). Deliberately NOT the step's `step_context`, which is the
   * one-line summary the guide recognises a described step by and is never
   * rendered.
   *
   * **MARKDOWN, and routinely multi-line** (2026-08-27): the play panel renders
   * it through `MarkdownView`. Carried verbatim — a consumer that needs plain
   * text must strip it rather than expect this to, and
   * `models/utils/markdownSpeech.ts` is what it should strip it WITH: that
   * shares the panel's parser, so a second reading of the same line cannot
   * disagree with the panel about what is markup.
   */
  readonly instructions: string;
  /** False when the step has no instructions — emptiness is the hidden state. */
  readonly showInstructions: boolean;
  /**
   * The Back button's label, or null when the author wrote none — which draws
   * the button as its arrow alone, NOT as nothing. Back's presence is
   * {@link hasPrevious}: the author labels the button, navigation decides
   * whether there is one.
   */
  readonly backButtonLabel: string | null;
  /** The Next button's label, or null when the step has NO Next button (empty label = hidden). */
  readonly nextButtonLabel: string | null;
  /**
   * Whether Back has anywhere to go: whether the learner's PATH has an earlier
   * arrival on it, NOT whether an earlier step exists in the document. A
   * trigger that branched them over five steps means Back returns to the step
   * they branched FROM, not to a step they have never seen.
   *
   * This is also whether the Back button is DRAWN at all. It is deliberately
   * not the disabled-but-visible treatment Next gets from
   * {@link isNextBlocked}: Next's block is temporary and lifts while the
   * learner watches, so showing it says "there is a way on, wait"; there is
   * nothing behind the first arrival of a path and there never will be, so an
   * inert Back there would be a control that can only refuse the press — and
   * since an unlabelled Back is now the default, it would be a bare arrow doing
   * nothing on the opening step of most runs.
   */
  readonly hasPrevious: boolean;
  /**
   * Whether another step FOLLOWS this one in the document. Deliberately not
   * "will Next move them": on a completion step it can be true while Next ends
   * the run, which is what {@link isCompletionStep} answers. Keeping the two
   * apart is what lets the panel say which of the two a press will do.
   */
  readonly hasNext: boolean;
  /**
   * Whether pressing Next ENDS the run here (the step's `is_completion_step`).
   * The panel titles the button from it; the press itself is the run model's
   * call, so nothing here has to decide.
   */
  readonly isCompletionStep: boolean;
  /**
   * Whether Next is held back right now by the step's `completion_condition` —
   * its delay has not elapsed, its script is still playing, or its video has
   * not finished. False for every
   * step that has no condition, which is the default, and false on a REVISIT:
   * the condition guards the learner's first pass through a step, so covered
   * ground is never gated again.
   *
   * The panel disables the button on this rather than hiding it: the learner
   * should see that there IS a way on and that it is not ready yet. It is a
   * different question from {@link nextButtonLabel} being null, which is the
   * author saying there is no Next button at all.
   */
  readonly isNextBlocked: boolean;
  /**
   * Why, in a phrase written to be read by a person and handed to the agent
   * verbatim. Null when Next is not blocked.
   */
  readonly nextBlockedReason: string | null;
  /** Trigger buttons from the step's `triggers` list, render order. */
  readonly triggers: ReadonlyArray<WorkflowPlaybackTriggerView>;
  /**
   * What the learner may reach right now — the step's `user_input_allowed`,
   * else the workflow's, else nothing. The editor shell hides each surface
   * that is not listed.
   *
   * Two independent capabilities rather than the one boolean this replaced:
   * `ai_input` opens Spark's composer, `tools` opens the scene tool palette —
   * which in this release is always EMPTY during a run: the learner has handed
   * the presenter role to Spark and holds no scene tool
   * (`IPermissionsModel.isPresenterDelegated`, AI-1426), so the shell mounts no
   * palette whatever this says.
   * The common case — let them ask a question, but keep them out of the scene —
   * could not be said at all before.
   *
   * RESOLVED, so a stored `all` arrives here already expanded into the concrete
   * kinds and a reader can simply test membership.
   */
  readonly userInputAllowed: ReadonlyArray<WorkflowUserInputKind>;
  /**
   * The workflow's `disable_mobile_keyboard`, resolved. Absent = false.
   *
   * On a phone the composer's tap cycle is listen → type; true takes the second
   * half out, so it is listen → stop and the field never focuses. Read by
   * `AiPanel` and by nothing else.
   *
   * It rides the PLAYBACK view rather than being read off the document, because
   * every other run fact the panel consumes arrives that way and a second
   * channel is a second thing to keep in step — even though this one is a
   * header value that cannot change between steps.
   */
  readonly isMobileKeyboardDisabled: boolean;
  /**
   * This step's on-start script has the FLOOR: it is still the thing doing the
   * talking, so anything else that speaks now talks over the author's lines.
   *
   * The raw latch, independent of any switch. Armed at arrival when the step
   * has an on-start script — synchronously, before the arrival's own emit, so
   * the panel's snapshot already carries it — and false for a step that has
   * none, because there is nothing to wait for.
   *
   * It clears on ANY terminal outcome, finished, stopped or failed alike, and
   * clears EARLY when the script parks on a `wait_for` with
   * `trigger: user_response` — the lesson has handed the learner the floor,
   * which is exactly when the script no longer holds it. Once cleared it stays
   * clear for the step.
   *
   * Its consumer is the agent side: {@link IAgentToolExecutorModel}'s workflow
   * executor reads it so the Guide's reply to a step move does not narrate over
   * a script that is about to speak.
   */
  readonly isOnStartScriptHoldingFloor: boolean;
  /**
   * Spark's composer is HELD BACK right now — the SHELL's question, and the
   * only one that decides whether the composer is drawn.
   *
   * **Always false as shipped.** It is
   * {@link isOnStartScriptHoldingFloor} gated by
   * `K_HOLD_AI_INPUT_DURING_ON_START_SCRIPT` in `WorkflowRunModel`, and that
   * flag is OFF by direction (2026-09-01): a step that opens `ai_input` shows
   * the composer from arrival, whatever its script or its
   * `completion_condition` are doing. See the flag for why the behaviour is
   * gated rather than deleted.
   *
   * A different question from {@link userInputAllowed} carrying `ai_input`,
   * and kept apart from it on {@link isNextBlocked}'s precedent: that list is
   * what the AUTHOR opened for this step, this is a temporary run-time hold on
   * one of its members. A consumer wanting "may the learner talk here at all"
   * reads the list; one deciding whether to DRAW the composer reads both.
   *
   * ⚠ A consumer asking whether the SCRIPT is still speaking wants
   * {@link isOnStartScriptHoldingFloor} instead — with the flag off this field
   * answers no on every step, including one mid-narration.
   */
  readonly isAiInputHeld: boolean;
  /** The step's scene attachment, or null when it points at nothing. */
  readonly attachment: WorkflowPlaybackAttachment | null;
  /**
   * The step's media, or null when it shows none. Null also when the authored
   * block has no url: a media panel with nothing to fetch is worse than no
   * panel, and `validate()` already reports the missing url.
   */
  readonly media: WorkflowPlaybackMedia | null;
  /**
   * The step is its MEDIA and nothing else: it names no scene, so there is no
   * 3D content it is about, and it carries media the learner is meant to look
   * at. The media panel expands to a centred, large presentation for these and
   * returns to its dock for every other step.
   *
   * Both terms are load-bearing. The trigger is the empty `scene` — a step that
   * names one is about that scene, and its media is reference material beside
   * it — but a step with no scene AND no media is just a text step, and calling
   * that "media-only" would be a flag nothing could act on. So this is exactly
   * the condition the panel expands under, rather than a fact the panel then
   * has to re-qualify with {@link media}.
   */
  readonly isMediaOnly: boolean;
}

/**
 * What the step the learner is on animates — the answer to "replay the
 * animation" when nobody names a model (AI-1459,
 * `WorkflowRunModel.describeCurrentStepAnimation`).
 */
export interface WorkflowStepAnimation {
  /**
   * No scene is on screen: a media-only step covers it, and so does a text
   * step after one until a step names a scene. Nothing there can animate.
   */
  readonly isSceneHidden: boolean;
  /**
   * Each take the step's own scripts PLAY, as authored — a
   * `Toggle_Model_Animation` step not passing `enabled: false`. Read from the
   * document rather than from what has run, so it is right while the step is
   * still narrating, before its script reaches the take.
   */
  readonly takes: ReadonlyArray<{ readonly partPath: string | null; readonly take: string | null }>;
}

/**
 * WHO moved the run, and with WHAT — carried by every navigation call and
 * announced on {@link IWorkflowRunModel.onStepArrived}.
 *
 * The two movers reach the Guide differently, which is why this has to be
 * known where the move happens. The agent's own tool reply already says where
 * it took the learner. A move the LEARNER makes on their panel reached the
 * agent only through the run-state bullets in its instructions — and the model
 * answers from its CONVERSATION as much as from its instructions. Measured
 * 2026-09-11: after two outline picks, "can you replay the step?" re-sent the
 * agent's own last named jump, 80 s old, over bullets that correctly named the
 * step the learner was on. So a learner's move is also SAID in the
 * conversation (`AIOrchestratorModel`), and the agent's is not said twice.
 *
 * Passed in rather than inferred from the method called: two callers reach the
 * same method for different reasons (the outline and the agent's named jump
 * both rewind through {@link IWorkflowRunModel.goBackToStep}).
 */
export type WorkflowMoveCause =
  | { readonly by: "learner"; readonly via: "next" | "back" | "outline" }
  | { readonly by: "agent"; readonly via: "next" | "back" | "step" | "replay" }
  /**
   * A trigger button, pressed by EITHER mover: the learner clicked it, or they
   * said its label out loud and the Guide pressed it for them
   * (`go_to_workflow_step trigger=`). One variant rather than two because the
   * button is the same authored branch whichever way it was reached — only the
   * `by` differs, and that is what decides whether the move is also said in
   * the conversation.
   *
   * `label` is the button as the learner saw it — runtime tokens resolved.
   */
  | { readonly by: "learner" | "agent"; readonly via: "trigger"; readonly label: string };

/** The `by` half of {@link WorkflowMoveCause} — who moved the run. */
export type WorkflowMover = WorkflowMoveCause["by"];

/** One arrival at a step, as {@link IWorkflowRunModel.onStepArrived} announces it. */
export interface WorkflowStepArrival {
  readonly stepName: string;
  /** The step the learner was on — the same name for a replay. */
  readonly fromStepName: string | null;
  /**
   * 1-based, over the PLAYABLE steps — the "n of m" the run-state bullets and
   * the executor's replies quote, so the three cannot disagree.
   */
  readonly position: number;
  readonly stepCount: number;
  readonly cause: WorkflowMoveCause;
}

/**
 * A run STARTING, as {@link IWorkflowRunModel.onRunStarted} announces it —
 * where the learner begins, in the same terms as {@link WorkflowStepArrival}.
 */
export interface WorkflowRunStart {
  readonly workflowName: string;
  /**
   * The non-English language the PREVIOUS run in this project spoke, which
   * this one does not carry over — or null. The conversation still holds that
   * run's talk in it, so the start has to say the choice is gone.
   */
  readonly previousSpeechLanguage: string | null;
  /** The language the workflow is written in, which this run starts in. */
  readonly workflowLanguage: string;
  /** The step the run starts on: the first playable one, or where remembered progress resumes it. */
  readonly stepName: string;
  /** 1-based, over the PLAYABLE steps, as in {@link WorkflowStepArrival}. */
  readonly position: number;
  readonly stepCount: number;
}

/**
 * How {@link IWorkflowRunModel.requestStartAsync} ended. A named outcome rather
 * than a boolean, because each one needs something different said to the
 * person who asked — and the wording belongs to the executor, not here.
 */
export type WorkflowStartOutcome =
  /** The run is playing now. */
  | "started"
  /**
   * Accepted, and parked on the wearer seating the stage first
   * (`requires_console_placement`); it plays once they are done.
   */
  | "awaiting_console_placement"
  /** A run was already playing, so there was nothing to start. */
  | "already_running"
  /** {@link IWorkflowRunModel.canStart} refused: no document, no playable step, or validation errors. */
  | "not_playable"
  /**
   * Accepted, and the start did not happen: it was refused or superseded while
   * it loaded (the document changed under it, a Stop landed, the user-state
   * read threw). Play mode has already been dropped again.
   */
  | "not_started";

/** Why a Viewer could not enter the guided workflow that is their project surface. */
export type WorkflowViewerStartFailureReason =
  | "not_playable"
  | "start_failed";

export interface WorkflowViewerStartFailure {
  readonly projectId: string;
  readonly reason: WorkflowViewerStartFailureReason;
}

export interface IWorkflowRunModel {
  /** Raised when the run starts/stops or the current step changes. */
  readonly onRunStateChanged: Emitter<void>;

  /**
   * Raised once per ARRIVAL at a step — Next, Back, a trigger button, an
   * outline pick, and every agent move including a replay — with who caused
   * it. Not raised by the run's start (its first step is not an arrival — see
   * {@link onRunStarted}) nor by the move that finishes it.
   *
   * Fires AFTER the arrival's {@link onRunStateChanged}, so the state is final,
   * and BEFORE the step's on-start script starts, so a line a subscriber adds
   * to the conversation lands ahead of the step's own narration.
   */
  readonly onStepArrived: Emitter<WorkflowStepArrival>;

  /**
   * Raised once per run START — Play, autostart, `start_workflow`, and a start
   * that waited for console placement — with the step it starts on. The
   * start's counterpart of {@link onStepArrived}, with the same ordering: AFTER
   * the start's {@link onRunStateChanged}, so the role and the state are final,
   * and BEFORE the first step's on-start script, so a line a subscriber adds to
   * the conversation lands ahead of that step's narration.
   *
   * It exists so a new run can be SAID to the Guide's conversation, which
   * outlives a run (AI-1419) — see `AIOrchestratorModel.handleWorkflowRunStarted`.
   */
  readonly onRunStarted: Emitter<WorkflowRunStart>;

  /**
   * The workflow finished and this user has nothing left to do in the project:
   * leave it and return to the projects grid.
   *
   * Raised for a VIEWER only — a non-author, for whom the project IS the
   * workflow. An author who previews their own run to the end stays put, or
   * testing it would eject them from the editor every time.
   *
   * An INTENT, not an action: this model is headless and routing is a view
   * concern (`Campfire_Web_Runtime.md:48`), so `ProjectView` subscribes and
   * navigates — the same shape as `IProjectModel.onActiveProjectEvicted`.
   * Emitted LAST, once the run is fully torn down and
   * {@link onRunStateChanged} has already fired, so nothing is half-stopped
   * while the route changes. Carries the project it belongs to, because a
   * subscriber has no other way to tell a late emit from a live one.
   */
  readonly onProjectExitRequested: Emitter<{ projectId: string; workflowName: string }>;

  /**
   * Raised when automatic playback cannot start for a confirmed Viewer. The
   * model reports the failure; `ProjectView` owns the standard alert and route
   * back to Projects. The latest failure remains available through
   * {@link viewerStartFailure} so a view mounted after the emit still handles
   * it.
   */
  readonly onViewerStartFailed: Emitter<WorkflowViewerStartFailure>;

  /** The current project's Viewer start failure, or null. */
  readonly viewerStartFailure: WorkflowViewerStartFailure | null;

  readonly isRunning: boolean;
  /**
   * A start is parked on the wearer finishing console placement
   * (`requires_console_placement`). True only on a headset, only between Play
   * and the mode closing, and never at the same time as {@link isRunning}.
   *
   * Exposed so the placement slate can say WHY it is up: a wearer who opened
   * placement themselves needs no explanation, but one who pressed Play and
   * landed here does. Changes raise {@link onRunStateChanged}.
   */
  readonly isAwaitingConsolePlacement: boolean;
  /**
   * The 3D scene is kept OFF the learner's screen: the viewport shows its empty
   * background instead (user direction, 2026-10-01: "when the user is on a image
   * or video step, the scene of the previous step is unloaded and the background
   * is empty").
   *
   * Set on arriving at a MEDIA-ONLY step ({@link WorkflowPlaybackView.isMediaOnly}),
   * whose scene is whichever one the previous step left active — scenery the
   * step is not about, which used to show through the blur behind the media and
   * then flash back sharp, or morph into the next scene, on the way out.
   *
   * It OUTLIVES that step, and that is the point. It ends only once a step that
   * names a scene has that scene active AND built, so the learner goes from the
   * empty background straight to the incoming scene and never sees the old one
   * in between. A step that names no scene — or names one the project does not
   * have — keeps it, because "play where the learner is" now means nowhere.
   * Stopping the run keeps it until the author is back on the first scene.
   *
   * A hide rather than an unload, deliberately: the web keeps every visited
   * scene's content and only toggles which one is drawn, so dropping the scene
   * would free nothing, and a null active scene is how the app announces that
   * the whole PROJECT is closing. The active scene is unchanged throughout.
   *
   * Never persisted and never true outside a run's lifetime except across that
   * one return to the first scene. Changes raise {@link onRunStateChanged}.
   */
  readonly isSceneHidden: boolean;
  readonly currentStepName: string | null;
  /**
   * The language the learner asked Spark to speak on the step they are on
   * NOW (`"Spanish"`): a "this step only" language while it covers this step,
   * else the run's switch, else null — which means the workflow's own
   * language. A switch BACK is kept as a value of its own (`"English"`),
   * because the run-state bullets have to SAY the change rather than fall
   * silent (`Campfire_Web_Workflow.md` → "state the CHANGE"). Every run starts
   * at null; nothing persists it. See `workflowSpeechLanguage.ts`.
   */
  readonly speechLanguage: string | null;
  /**
   * The language step lines must be TRANSLATED into right now: the
   * {@link speechLanguage} when it differs from the one the workflow is
   * written in ({@link workflowLanguage}), else null. What tags a narration
   * line and what the agent's tool results are told to answer in.
   */
  readonly translationLanguage: string | null;
  /** The language the workflow is written in, from its `language:` field (English when absent). */
  readonly workflowLanguage: string;
  /**
   * Sets the language and returns the normalized name, or null when no run is
   * playing or nothing usable was passed. Raises {@link onRunStateChanged} when
   * it changes, so the prompt is pushed before the next narration.
   * - `run`: "switch to Spanish" — the rest of the run, until switched again.
   *   Ends any "this step only" language.
   * - `step`: "repeat this step in Spanish" — the step the learner is on, until
   *   the run lands on a different step (replaying this one keeps it).
   * - `arrival`: like `step`, for the step the NEXT arrival lands on — a move
   *   that also names a language. {@link clearPendingStepLanguage} drops it
   *   when the move is refused.
   */
  setSpeechLanguage(language: string, scope?: "run" | "step" | "arrival"): string | null;
  /** Drops an `arrival`-scoped language whose move did not happen. */
  clearPendingStepLanguage(): void;
  /**
   * The COVERED GROUND: every step this run has landed on, deduplicated, in
   * first-visit order. Monotonic — a rewind does not un-see a step — which is
   * why this, and not {@link visitStack}, decides where the learner may be
   * sent. Persisted in the per-user DynamoDB row, and restored on a new run
   * only when the workflow asks to be remembered (`remember_progress`).
   */
  readonly visitedStepNames: readonly string[];
  /**
   * The PATH: the ordered arrivals with duplicates kept, ending on the current
   * step, so `visitStack.length - 1` is how many steps back the learner can be
   * taken. Rewound by every back-navigation — see `WorkflowRunState.visitStack`.
   */
  readonly visitStack: readonly string[];
  /**
   * The step the learner was on when a rewind interrupted them (or, once the
   * run has stopped, where it stopped), so the Guide can offer to continue
   * rather than guess. Null when they are not behind their own furthest point.
   */
  readonly resumePointStepName: string | null;

  /** Current user's loaded/in-memory state, separate from workflow.yaml. */
  getUserState(): WorkflowRunState | null;

  /**
   * Whether pressing Play would do anything: a loaded workflow with at least
   * one step that validates clean. The Workflow panel's Preview button reads
   * this so it disables instead of silently refusing.
   */
  readonly canStart: boolean;

  /**
   * Loads the active user's state and starts when {@link canStart}. Returns the
   * workflow name, or null when not playable.
   */
  tryStart(): Promise<string | null>;

  /** Stops the run, saves the resume point, and stops any playing workflow scripts. */
  stop(): void;

  /**
   * End playback ON DEMAND — the agent's half of the run header's Exit button.
   *
   * Everything that button does, this does, because it does it the same way:
   * it flips `IProjectModel.isSceneStartPlayModeEnabled` and lets that flag
   * re-enter {@link stop}. So an EDITOR is left in the editor with the run
   * over — back on the project's first scene — and a VIEWER, for whom the
   * project IS the workflow, leaves the project too, via
   * {@link onProjectExitRequested}. Neither branch is decided
   * here; both are `stop()`'s, which is what stops the two surfaces drifting.
   *
   * ⚠ Do NOT "simplify" this into a direct `stop()` call. That flag is what the
   * editor shell, the tool palette and the Spark panel all watch, so ending the
   * run without it leaves the app in play mode with nothing playing — the same
   * reasoning `finishRunOnCompletionStep` records.
   *
   * False when there is nothing to stop: no run in progress, and no start
   * parked on console placement.
   */
  requestStop(reason: string): boolean;

  /**
   * START playback on demand — the agent's half of the Play button
   * (`start_workflow`), and {@link requestStop}'s twin.
   *
   * It presses the button rather than reimplementing it: it flips
   * `IProjectModel.isSceneStartPlayModeEnabled`, which is what the button and
   * autostart both do, so a spoken start is indistinguishable from a pressed
   * one — the same chrome hides, the same role takes over, and
   * {@link tryStart} still owns every refusal after the up-front one. What it
   * adds is WAITING for that start, so the caller can say what happened: the
   * flag's own start is fire-and-forget, because it reads the user's persisted
   * progress before the first step plays.
   *
   * ⚠ Do NOT "simplify" this into a direct `tryStart()` call, for the reason
   * {@link requestStop} gives in reverse: the editor shell, the tool palette
   * and the Spark panel all watch that flag, so a run started without it
   * plays under an editor that does not know it is playing.
   *
   * Never rejects. `not_playable` is decided up front from {@link canStart};
   * whatever goes wrong once the start is under way resolves `not_started`,
   * with play mode dropped again exactly as a refused Play press drops it.
   */
  requestStartAsync(reason: string): Promise<WorkflowStartOutcome>;

  /**
   * Whether the run has already landed on this step (case-insensitive). The
   * agent's named jumps are limited to these: it may take the learner back over
   * covered ground, but not ahead into a step they have not reached. The
   * learner's own buttons and a trigger's authored `go_to` are NOT limited this
   * way — branching forward is the author's decision to make.
   */
  hasVisitedStep(stepName: string): boolean;

  /**
   * Moves the run ONWARD to the named step — an authored trigger's `go_to`, or
   * any move that EXTENDS the learner's path. Appends to the visit stack, so a
   * step reached twice is on the path twice. Persists run state, activates the
   * step's scene and auto-plays its on-start script.
   *
   * Deliberately unrestricted: branching forward is the author's decision. The
   * "no jumping ahead" limit lives on the BACK seams below and in the executor.
   */
  goToStep(stepName: string, cause: WorkflowMoveCause): boolean;

  /**
   * Rewinds the run to the named step — the learner's outline pick or the
   * agent's named jump. Truncates the visit stack at that step's LAST arrival
   * (so the steps they returned from leave the path and cannot be walked back
   * to in a loop), and records where they were as the resume point.
   *
   * Refused for a step this run has never reached: going BACK means covered
   * ground by definition. A covered step that is no longer on the path (its
   * entries were rewound away earlier) is appended instead of truncated to —
   * there is no route left to rewind, but the move is still legal.
   */
  goBackToStep(stepName: string, cause: WorkflowMoveCause): boolean;

  /**
   * "Let's go back three steps." Rewinds exactly `count` arrivals off the
   * learner's path — the steps they were actually at, in the order they were at
   * them, whatever branching or repeats put them there.
   *
   * False when `count` is not a positive whole number, or when the path is not
   * that deep; the caller reports the real depth rather than landing them
   * somewhere they did not ask for.
   */
  goBackSteps(count: number, cause: WorkflowMoveCause): boolean;

  /**
   * "Show me that again" about the step the learner is ALREADY on. Re-arrives
   * at the current step, so it plays exactly as it did the first time: the
   * outgoing narration stops, the step's video goes back to its start, and its
   * on-start script plays again — scene, step view, `say` lines, `play_video`
   * and all.
   *
   * ⚠ This, not `play_workflow_script`, is how an agent replays a step during a
   * run. A script belongs to its step, and replaying the STEP is what keeps the
   * step's own order, its media reset and its gate intact. The agent reaches it
   * with `go_to_workflow_step direction=replay`, which takes NO step name — so
   * "do this step again" cannot be resolved to the wrong step.
   *
   * The visit stack is unchanged (a replay is not a move), and the step's Next
   * gate is re-armed only when it was still PENDING — a replay must not waive a
   * wait the learner has not sat through, nor charge again a wait they have.
   *
   * False when no run is playing or the current step is gone.
   */
  replayCurrentStep(cause: WorkflowMoveCause): boolean;

  /** The panel's render state for the current playback moment; null when not running. */
  getPlaybackView(): WorkflowPlaybackView | null;

  /**
   * The learner pressed Next: COMPLETES the current step — any playing script
   * stops and the run advances immediately. Next is pure navigation; there is
   * no completion script to wait on.
   *
   * False while the step's `completion_condition` still holds it back
   * ({@link WorkflowPlaybackView.isNextBlocked}), so a caller that bypassed the
   * disabled button cannot bypass the gate either.
   */
  advanceToNextStep(cause: WorkflowMoveCause): boolean;

  /**
   * The learner pressed Back: rewinds ONE arrival off their path — the step
   * they were on before this one, not the previous step in document order —
   * WITHOUT completing the current one. False at the start of the path.
   */
  goToPreviousStep(cause: WorkflowMoveCause): boolean;

  /**
   * One of the current step's trigger buttons was pressed (addressed by its
   * source-list index — see {@link WorkflowPlaybackTriggerView.index}):
   * navigates to its `go_to` target (a group resolves to its first step)
   * WITHOUT completing the current step.
   *
   * Takes the MOVER rather than a whole {@link WorkflowMoveCause} because the
   * cause's other half — the resolved label — is read off the trigger here,
   * and a caller passing its own could name a button other than the one the
   * index presses.
   *
   * `"agent"` is the learner SAYING the button instead of clicking it: on a
   * step whose only way out is a trigger, the alternative was the Guide having
   * no move at all (AI-1352). It actuates an authored branch on the step the
   * learner is already on, so it stays inside the same limit the named jump
   * enforces — the agent still cannot choose a forward destination of its own.
   */
  activateTrigger(triggerIndex: number, movedBy: WorkflowMover): boolean;

  /**
   * The current step's media video reached its end — what opens a
   * `video_finished` completion condition.
   *
   * A push from the UI, the direction `IProjectModel.reportSceneBuild*` already
   * establishes: the `<video>` element is the media panel's and this model must
   * never import a renderer, but it is the model that decides whether the
   * learner may move on. Reported for ANY terminal outcome — the clip ran out,
   * the authored trim's end was reached, or the media could not be loaded at
   * all — for the same reason a failed script still opens `script_finished`: a
   * learner stranded by a broken reference has no way to know or fix it.
   *
   * Scoped by step name, and free to call repeatedly: a report for a step that
   * is not current, or for a gate of a different kind, is ignored, and the gate
   * only opens once.
   */
  reportStepMediaFinished(stepName: string): void;

  /**
   * Registers the media panel's playback controls and returns the release for
   * THIS registration.
   *
   * ⚠ A returned disposer rather than `set…(host | null)` — the shape
   * `Emitter.on` already uses — because the identity guard is what makes the
   * swap safe. Exactly one media panel is mounted at a time, but entering or
   * leaving XR swaps `ProjectView`'s copy for `XRPanelHost`'s in one patch and
   * nothing orders the outgoing component's teardown after the incoming one's
   * setup. A bare `set(null)` on dispose would then wipe the registration that
   * had just replaced it, and the failure is silent: every command answers
   * `unavailable` until the panel remounts.
   */
  attachMediaPlaybackHost(host: IWorkflowMediaPlaybackHost): () => void;

  /**
   * Runs one {@link WorkflowMediaCommand} against the current step's video —
   * `control_media`'s whole implementation.
   *
   * Refuses in the model rather than in the executor for everything the model
   * knows: whether a run is playing, and whether the current step shows a video
   * at all. The panel is asked only about the element.
   */
  controlStepMedia(command: WorkflowMediaCommand): WorkflowMediaControlResult;

  /**
   * Registers the workflow outline panel's toggle and returns the release for
   * THIS registration — see {@link IWorkflowOutlineHost}, including why an
   * outline that also exists outside a run is reachable from here.
   */
  attachOutlineHost(host: IWorkflowOutlineHost): () => void;

  /**
   * Shows or hides the workflow outline — `Toggle_Workflow_Outline`'s whole
   * implementation. Null when no workflow panel is mounted to command; the
   * returned state is read AFTER the change, since the flag is set
   * synchronously.
   */
  controlOutline(visible: boolean): WorkflowOutlineState | null;

  /**
   * Run-state bullets for the runtime-conversation prompt section, or null when
   * the project has no workflow: where the run is, what ground is covered, and
   * the current step's trigger buttons. The DOCUMENT is not part of this — the
   * roles that may read it pull it with `get_workflow`.
   */
  buildRuntimePromptInstructions(): string | null;

  dispose(): void;
}

export const WORKFLOW_RUN_MODEL = Symbol.for("Campfire.WorkflowRunModel");
